(ns ui.table
  (:require [lucide-react :refer [ArrowUpDown ArrowUp]]
            [ui.table-widths :refer [clamp-width store-column-widths!]]
            [ui.utils :refer [clsx separate-props]]))

(defn table
  [& args]
  (let [[{:keys [sticky-header] :as props} children] (separate-props args)]
    (into [:table (-> props
                      (dissoc :sticky-header)
                      (update :class #(clsx "w-full table border-spacing-0"
                                            (if sticky-header "border-separate" "border-collapse")
                                            %)))]
          children)))

(defn tbody
  [& args]
  (let [[props children] (separate-props args)]
    (into [:tbody props]
          children)))

(defn thead
  [& args]
  (let [[props children] (separate-props args)]
    (into [:thead (update props :class #(clsx "table-header-group text-kleene-text-secondary bg-kleene-background text-left *:h-auto"
                                              %))]
          children)))

(defn tr
  [& args]
  (let [[props children] (separate-props args)]
    (into [:tr (-> props
                   (dissoc :sticky)
                   (update :class #(clsx "h-12"
                                         {"*:sticky *:z-10 top-0" (:sticky props)}
                                         %)))]
          children)))

(defn th
  [& args]
  (let [[props children] (separate-props args)]
    (into [:th (update props
                       :class #(clsx "relative px-3 py-1.5 bg-kleene-background font-medium border-b border-kleene-layout-border"
                                     %))]
          children)))

(defn td
  [& args]
  (let [[{:keys [no-padding ellipsis] :as props} children] (separate-props args)]
    (into [:td (-> props
                   (dissoc :no-padding :ellipsis)
                   (update :class #(clsx "border-b border-kleene-layout-border align-middle"
                                         {"px-3 py-1.5" (not no-padding)
                                          "overflow-hidden whitespace-nowrap max-w-full text-ellipsis" ellipsis}
                                         %)))]
          children)))

(defn sort-label
  [{:keys [active direction] :as props} & children]
  (let [icon (if active ArrowUp ArrowUpDown)
        direction (some-> direction name .toLowerCase)]
    [:div (-> props
              (dissoc :active :direction)
              (update :class #(clsx "flex justify-between cursor-pointer items-center group" %)))
     (into [:span {:class "min-w-0"}] children)
     [:> icon {:class (clsx "shrink-0 group-hover:text-kleene-text-secondary transition-all"
                            (if active "text-kleene-text-secondary/80" "text-kleene-selected-background")
                            (when (and active (= direction "desc")) "rotate-180"))}]]))


(def ^:private min-column-width 64)

(defn resizable-th
  "`th` sized from a `column-widths` map, with a drag handle on its right edge.
   `:on-resize` gets the next map as the pointer moves; the final one is persisted
   when the drag ends. Only the header needs sizing — `table-fixed` takes the
   column widths from the first row."
  [{:keys [id widths storage-key on-resize min-width max-width]
    :or   {min-width min-column-width}
    :as   props} & children]
  (let [fit   #(clamp-width % min-width max-width)
        width (some-> (get widths id) fit)]
    (into [th (-> props
                  (dissoc :id :widths :storage-key :on-resize :min-width :max-width)
                  (assoc :style (merge {:width width} (:style props))))]
          (conj (vec children)
                [:div {:class "absolute right-0 inset-y-0 w-1 cursor-col-resize select-none touch-none hover:bg-kleene-secondary-01/40"
                       :on-pointer-down
                       (fn [e]
                         (when (zero? (.-button e))
                           (.preventDefault e)
                           (.setPointerCapture (.-currentTarget e) (.-pointerId e))
                           ;; One handle moves one column, so the map at pointerdown is
                           ;; the base for the whole gesture — nothing newer to read,
                           ;; which is what lets this work from a value.
                           (let [start-x     (.-clientX e)
                                 start-width width
                                 body        (.. js/document -body -classList)
                                 latest      (volatile! nil)]
                             (.add body "select-none" "cursor-col-resize")
                             (letfn [(move [ev]
                                       (vreset! latest (assoc widths id (fit (+ start-width (- (.-clientX ev) start-x)))))
                                       (when on-resize (on-resize @latest)))
                                     (done [_]
                                       (.removeEventListener js/document "pointermove" move)
                                       (.removeEventListener js/document "pointerup" done)
                                       (.removeEventListener js/document "pointercancel" done)
                                       (.removeEventListener js/document "lostpointercapture" done)
                                       (.remove body "select-none" "cursor-col-resize")
                                       (when-let [moved @latest]
                                         (store-column-widths! storage-key moved)))]
                               (.addEventListener js/document "pointermove" move)
                               (.addEventListener js/document "pointerup" done)
                               (.addEventListener js/document "pointercancel" done)
                               (.addEventListener js/document "lostpointercapture" done)))))}]))))
